import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { VendorFilterChip } from '@/components/vulnerabilities/VendorFilterChip'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { SeverityBadge } from '@/components/shared/SeverityBadge'
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable'
import { useVulnerabilities, useVulnerabilitySeverityKpis } from '@/hooks/queries'
import { useFilterDefaultsFromPrefs } from '@/hooks/useFilterDefaultsFromPrefs'
import { formatEpssDisplay } from '@/lib/epss-display'
import { formatDate } from '@/lib/utils'
import type {
  Severity,
  ExploitationStatus,
  Vulnerability,
  VulnerabilityFilters,
} from '@/lib/types'

const EXPLOITATION_LABELS: Record<ExploitationStatus, string> = {
  none: 'None',
  poc: 'PoC',
  active: 'Active',
  widespread: 'Widespread',
}

const EXPLOITATION_STYLES: Record<ExploitationStatus, string> = {
  none: 'text-green-600 dark:text-green-400',
  poc: 'text-yellow-600 dark:text-yellow-400',
  active: 'text-orange-600 dark:text-orange-400',
  widespread: 'text-red-600 dark:text-red-400',
}

// Sort UI ship-or-delete (P4b spec line 78): DELETED. Previously this page held
// dead `useState<SortField>` + `useState<sortAsc>` with no UI affordances and
// `eslint-disable @typescript-eslint/no-unused-vars` on the setters. The route
// owns a deterministic server-side order:
//   1. CVSS-populated rows first (NULLs last)
//   2. severity bucket (critical → low)
//   3. first_seen DESC NULLS LAST
//   4. EPSS presence/score as a tie-breaker
//   5. last_updated DESC NULLS LAST.
// Client-side re-sorting would mis-order a paginated subset relative to the
// server's view. If a future spec needs analyst-driven sort, it can be
// reintroduced as a `?sort=` query alongside the existing `limit/offset`
// envelope; deleting the dead state here removes the "shipped silently dead"
// risk the audit (item 10) flagged.

const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low']

const SEVERITY_KPI_STYLES: Record<Severity, string> = {
  critical: 'text-red-600 dark:text-red-400',
  high: 'text-orange-600 dark:text-orange-400',
  medium: 'text-yellow-600 dark:text-yellow-400',
  low: 'text-green-600 dark:text-green-400',
}

export function VulnerabilitiesPage() {
  // P5 adoption (Wave-1, rule §10): seed filter state from the user's saved
  // search row keyed on `'vulnerabilities'`. Returns `undefined` for
  // unauthenticated users or users with no matching saved row, in which case
  // we fall back to "all" defaults — Captain Input #4 (no synthesised
  // defaults, no migration prompt for existing users).
  const { defaults: savedDefaults } =
    useFilterDefaultsFromPrefs<VulnerabilityFilters>('vulnerabilities')

  const [search, setSearch] = useState<string>(
    typeof savedDefaults?.search === 'string' ? savedDefaults.search : '',
  )
  const [severity, setSeverity] = useState<string>(
    typeof savedDefaults?.severity === 'string' ? savedDefaults.severity : 'all',
  )
  const [exploitStatus, setExploitStatus] = useState<string>(
    typeof savedDefaults?.exploitation_status === 'string'
      ? savedDefaults.exploitation_status
      : 'all',
  )
  const [patchStatus, setPatchStatus] = useState<string>(
    typeof savedDefaults?.patch_status === 'string'
      ? savedDefaults.patch_status
      : 'all',
  )

  // Phase B T11: vendor filter from `?vendor=<uuid>` URL param.
  const [searchParams] = useSearchParams()
  const vendorParam = searchParams.get('vendor') || undefined

  const { data: vulns, isLoading } = useVulnerabilities({
    search: search || undefined,
    severity: severity !== 'all' ? severity : undefined,
    exploitation_status: exploitStatus !== 'all' ? exploitStatus : undefined,
    patch_status: patchStatus !== 'all' ? patchStatus : undefined,
    vendor: vendorParam,
  })

  // Severity KPI counts come from a server-side aggregate, NOT a reduce over
  // the LIMIT-capped list response. Pre-fix the rail showed Critical 50 /
  // others 0 because the list returns at most 50 rows ordered severity-DESC,
  // so non-critical buckets always read 0 (smoke 2026-05-03).
  const severityKpisQuery = useVulnerabilitySeverityKpis()
  const severityCounts = useMemo<Record<Severity, number>>(
    () =>
      severityKpisQuery.data ?? {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
      },
    [severityKpisQuery.data],
  )

  return (
    <main className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Vulnerabilities</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            CVE tracking with CVSS and EPSS scoring.
          </p>
        </div>
      </header>

      {/* KPI rail — severity counts */}
      <div
        data-testid="kpi-rail"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        {SEVERITY_ORDER.map((s) => (
          <div
            key={s}
            className="rounded-lg border border-border bg-card px-4 py-3"
          >
            <p className="text-xs uppercase tracking-wide text-muted-foreground capitalize">
              {s}
            </p>
            <p
              className={`text-2xl font-semibold tabular-nums mt-0.5 ${SEVERITY_KPI_STYLES[s]}`}
            >
              {severityCounts[s]}
            </p>
          </div>
        ))}
      </div>

      {/* Filter rail */}
      <div
        className="flex flex-wrap gap-2"
        data-testid="vulnerabilities-filter-rail"
      >
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search CVEs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="vulnerabilities-filter-search"
          />
        </div>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger
            className="w-36"
            data-testid="vulnerabilities-filter-severity"
          >
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {(['critical', 'high', 'medium', 'low'] as Severity[]).map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={exploitStatus} onValueChange={setExploitStatus}>
          <SelectTrigger
            className="w-40"
            data-testid="vulnerabilities-filter-exploitation"
          >
            <SelectValue placeholder="Exploitation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All exploitation</SelectItem>
            {(['none', 'poc', 'active', 'widespread'] as ExploitationStatus[]).map(
              (s) => (
                <SelectItem key={s} value={s}>
                  {EXPLOITATION_LABELS[s]}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        <Select value={patchStatus} onValueChange={setPatchStatus}>
          <SelectTrigger
            className="w-36"
            data-testid="vulnerabilities-filter-patch"
          >
            <SelectValue placeholder="Patch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All patch status</SelectItem>
            <SelectItem value="patched">Patched</SelectItem>
            <SelectItem value="unpatched">Unpatched</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Phase B T11: vendor filter chip — visible when ?vendor=<uuid> is set */}
      {vendorParam && (
        <div>
          <VendorFilterChip vendorId={vendorParam} />
        </div>
      )}

      {/* Table */}
      <section>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <DataTable<Vulnerability>
            columns={VULN_COLUMNS}
            rows={vulns ?? []}
            pageSize={25}
            searchable={false}
            emptyTitle="No vulnerabilities match your filters."
          />
        )}
      </section>
    </main>
  )
}

const VULN_COLUMNS: DataTableColumn<Vulnerability>[] = [
  {
    key: 'cveId',
    header: 'CVE ID',
    sortable: false,
    render: (v) => {
      // Phase 2 T11+T12: top 1-2 vendor `VendorRef[]` inline below the title.
      // Server pre-sorts alpha + caps at 2 (see vulnerabilities.routes.ts T9);
      // empty case renders nothing per spec §6.3. Tier-1 vendors carry a
      // small "T1" marker before the name so the elevated platform-vendor
      // signal is visible at the list level.
      const top = v.topAffectedVendors ?? []
      const total = v.affectedVendorCount ?? 0
      return (
        <div>
          <Link
            to={`/vulnerabilities/${v.id}`}
            className="font-mono text-primary hover:underline font-medium"
          >
            {v.cveId}
          </Link>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{v.title}</p>
          {top.length > 0 && (
            <p
              className="text-xs text-muted-foreground mt-0.5 line-clamp-1"
              data-testid="vuln-card-vendors"
            >
              {top.map((vv, i) => (
                <span key={vv.id}>
                  {i > 0 && ', '}
                  {vv.tier === 1 && (
                    <span
                      className="mr-1 rounded-sm bg-primary/15 px-1 text-[10px] font-semibold uppercase tracking-wide text-primary"
                      data-testid="vuln-card-vendor-tier-1-pill"
                      aria-label="Tier 1 vendor"
                    >
                      T1
                    </span>
                  )}
                  {vv.name}
                </span>
              ))}
              {total > 2 && ` +${total - 2} more`}
            </p>
          )}
        </div>
      )
    },
  },
  {
    key: 'cvssScore',
    header: 'CVSS',
    sortable: false,
    render: (v) => (
      <span className="tabular-nums font-semibold">
        {v.cvssScore ? parseFloat(v.cvssScore as string).toFixed(1) : '—'}
      </span>
    ),
  },
  {
    key: 'epssScore',
    header: 'EPSS',
    sortable: false,
    render: (v) => {
      const epssDisplay = formatEpssDisplay(v)
      const className = epssDisplay.isScore
        ? 'tabular-nums font-medium text-muted-foreground'
        : 'text-xs font-medium text-muted-foreground'

      return (
        <span className={className} title={epssDisplay.title || undefined}>
          {epssDisplay.label}
        </span>
      )
    },
  },
  {
    key: 'severity',
    header: 'Severity',
    sortable: false,
    render: (v) =>
      v.severity ? (
        <SeverityBadge severity={v.severity as Severity} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'exploitationStatus',
    header: 'Exploitation',
    sortable: false,
    render: (v) =>
      v.exploitationStatus ? (
        <span
          className={`text-xs font-medium ${EXPLOITATION_STYLES[v.exploitationStatus as ExploitationStatus]}`}
        >
          {EXPLOITATION_LABELS[v.exploitationStatus as ExploitationStatus]}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'patchAvailable',
    header: 'Patch',
    sortable: false,
    render: (v) => (
      <span
        className={`text-xs font-medium ${
          v.patchAvailable
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400'
        }`}
      >
        {v.patchAvailable ? 'Available' : 'No patch'}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Published',
    sortable: false,
    render: (v) => (
      <span className="text-xs text-muted-foreground">
        {formatDate((v.firstSeen ?? v.createdAt) as string)}
      </span>
    ),
  },
  {
    key: 'updatedAt',
    header: 'Updated',
    sortable: false,
    render: (v) => (
      <span className="text-xs text-muted-foreground">
        {formatDate(v.updatedAt)}
      </span>
    ),
  },
]
